/**
 * E2E-only insertable-stream impairment for the product-path adverse suite.
 *
 * Patches RTCPeerConnection.addTrack so video senders get createEncodedStreams
 * at addTrack time (late RTCRtpScriptTransform is a no-op once media flows).
 * Mutate window.__playcastBeaconImpair.cfg from Playwright to apply delay/loss.
 *
 * Gated by BEACON_WEBRTC_E2E_HOOKS — never installs in production.
 */

import {
  BEACON_WEBRTC_E2E_HOOKS_ENV,
  PLAYCAST_BEACON_IMPAIR_WINDOW_KEY,
  isBeaconE2eHooksEnabled,
} from '@playcastdotio/PeerCore';

export type BeaconImpairCfg = {
  delayMs: number;
  lossPct: number;
  jitterMs: number;
  dropKeyframes: boolean;
};

export type BeaconImpairStats = {
  seen: number;
  written: number;
  dropped: number;
};

export type PlaycastBeaconImpairApi = {
  cfg: BeaconImpairCfg;
  stats: BeaconImpairStats;
};

type EncodedStreamsSender = RTCRtpSender & {
  createEncodedStreams?: () => {
    readable: ReadableStream;
    writable: WritableStream;
  };
  __playcastBeaconImpairInstalled?: boolean;
};

type EncodedFrameLike = {
  type?: string;
};

type RtcPeerConnectionConstructor = new (
  ...args: ConstructorParameters<typeof RTCPeerConnection>
) => RTCPeerConnection;

let installed = false;
let originalRtcPeerConnection: RtcPeerConnectionConstructor | null = null;

const cfg: BeaconImpairCfg = {
  delayMs: 0,
  lossPct: 0,
  jitterMs: 0,
  dropKeyframes: false,
};

const stats: BeaconImpairStats = {
  seen: 0,
  written: 0,
  dropped: 0,
};

function readHooksEnv(): string | undefined {
  if (typeof process !== 'undefined' && process.env) {
    return process.env[BEACON_WEBRTC_E2E_HOOKS_ENV];
  }
  return undefined;
}

function resetCfgAndStats(): void {
  cfg.delayMs = 0;
  cfg.lossPct = 0;
  cfg.jitterMs = 0;
  cfg.dropKeyframes = false;
  stats.seen = 0;
  stats.written = 0;
  stats.dropped = 0;
}

function installOnSender(sender: RTCRtpSender): void {
  const anySender = sender as EncodedStreamsSender;
  if (anySender.__playcastBeaconImpairInstalled) return;
  if (typeof anySender.createEncodedStreams !== 'function') return;

  try {
    const { readable, writable } = anySender.createEncodedStreams();
    const transform = new TransformStream({
      async transform(frame: EncodedFrameLike, controller: TransformStreamDefaultController) {
        stats.seen++;
        const delay =
          (cfg.delayMs || 0) + (cfg.jitterMs > 0 ? Math.random() * cfg.jitterMs : 0);
        if (delay > 0) {
          await new Promise((r) => setTimeout(r, delay));
        }
        if (Math.random() * 100 < (cfg.lossPct || 0)) {
          const isKey = frame && frame.type === 'key';
          if (!isKey || cfg.dropKeyframes) {
            stats.dropped++;
            return;
          }
        }
        stats.written++;
        controller.enqueue(frame);
      },
    });
    readable.pipeThrough(transform).pipeTo(writable).catch(() => {});
    anySender.__playcastBeaconImpairInstalled = true;
  } catch {
    // Too late or unsupported — leave sender alone
  }
}

function patchRtcPeerConnection(): void {
  if (typeof window === 'undefined' || !window.RTCPeerConnection) return;
  if (originalRtcPeerConnection) return;

  const Original = window.RTCPeerConnection;
  originalRtcPeerConnection = Original;

  const Patched = function (
    this: RTCPeerConnection,
    ...args: ConstructorParameters<typeof RTCPeerConnection>
  ): RTCPeerConnection {
    const pc = new Original(...args);
    const origAddTrack = pc.addTrack.bind(pc);
    pc.addTrack = (track: MediaStreamTrack, ...streams: MediaStream[]) => {
      const sender = origAddTrack(track, ...streams);
      if (track && track.kind === 'video') {
        installOnSender(sender);
      }
      return sender;
    };
    return pc;
  } as unknown as RtcPeerConnectionConstructor;

  Patched.prototype = Original.prototype;
  Object.assign(Patched, Original);
  (window as unknown as { RTCPeerConnection: RtcPeerConnectionConstructor }).RTCPeerConnection =
    Patched;
}

function exposeWindowApi(): PlaycastBeaconImpairApi {
  const api: PlaycastBeaconImpairApi = { cfg, stats };
  (window as unknown as Record<string, unknown>)[PLAYCAST_BEACON_IMPAIR_WINDOW_KEY] = api;
  return api;
}

/**
 * Install insertable impairment bridge when BEACON_WEBRTC_E2E_HOOKS is enabled.
 * Safe to call multiple times. Must run before the first RTCPeerConnection.
 */
export function installBeaconInsertableImpairmentBridge(): PlaycastBeaconImpairApi | null {
  if (typeof window === 'undefined') return null;
  if (!isBeaconE2eHooksEnabled(readHooksEnv())) return null;

  if (installed) {
    return exposeWindowApi();
  }

  resetCfgAndStats();
  patchRtcPeerConnection();
  installed = true;
  return exposeWindowApi();
}

/**
 * Remove window.__playcastBeaconImpair and restore the original RTCPeerConnection.
 */
export function detachBeaconInsertableImpairmentBridge(): void {
  if (typeof window === 'undefined') return;

  delete (window as unknown as Record<string, unknown>)[PLAYCAST_BEACON_IMPAIR_WINDOW_KEY];

  if (originalRtcPeerConnection) {
    (window as unknown as { RTCPeerConnection: RtcPeerConnectionConstructor }).RTCPeerConnection =
      originalRtcPeerConnection;
    originalRtcPeerConnection = null;
  }

  installed = false;
  resetCfgAndStats();
}
